<script setup lang="ts">
import usePlugin from '@/composables/plugin'

interface GooglePlacePrediction {
  place_id: string
  structured_formatting: {
    main_text: string
    secondary_text: string
  }
}

declare global {
  interface Window {
    google: {
      maps: {
        places: {
          AutocompleteService?: new () => {
            getPlacePredictions: (
              request: Record<string, unknown>,
              callback: (results: GooglePlacePrediction[] | null, status: string) => void
            ) => void
          }
          AutocompleteSessionToken: new () => unknown
        }
      }
    }
  }
}

const modelValue = defineModel<string | null>({ required: true })

const { t } = useI18n()
const { installGoogleMapsAPI } = usePlugin()

const autocompleteString = ref('')
const apiSessionToken = ref<unknown>(null)
const open = ref(false)
const predictions = ref<GooglePlacePrediction[]>([])
const anchorRef = ref<HTMLElement | null>(null)

const hasPlaceId = computed(() => Boolean(modelValue.value))

const selectPrediction = (prediction: GooglePlacePrediction) => {
  const placeId = prediction?.place_id
  if (!placeId) return

  modelValue.value = placeId
  open.value = false
  autocompleteString.value = ''
  predictions.value = []
  apiSessionToken.value = null
}

const handleInputChange = (value: string | number | boolean | undefined) => {
  autocompleteString.value = typeof value === 'string' ? value : ''
  void fetchPredictions()
}

const fetchPredictions = async () => {
  const searchValue = autocompleteString.value.trim()

  if (searchValue.length <= 3) {
    open.value = false
    apiSessionToken.value = null
    predictions.value = []
    return
  }

  const AutocompleteService = window.google?.maps?.places?.AutocompleteService
  if (!AutocompleteService) {
    console.error('Google Places API not loaded')
    return
  }

  try {
    if (apiSessionToken.value === null) {
      apiSessionToken.value = new window.google.maps.places.AutocompleteSessionToken()
    }

    const service = new AutocompleteService()
    service.getPlacePredictions(
      {
        input: searchValue,
        types: ['establishment'],
        sessionToken: apiSessionToken.value
      },
      (results, status) => {
        if (status === 'OK' && results?.length) {
          predictions.value = results.filter((result) => Boolean(result.place_id))
          open.value = predictions.value.length > 0
          return
        }
        predictions.value = []
        open.value = false
      }
    )
  } catch (error) {
    console.error('Error fetching place predictions:', error)
    predictions.value = []
    open.value = false
  }
}

const renewGooglePlaceId = () => {
  modelValue.value = null
  open.value = false
  autocompleteString.value = ''
  predictions.value = []
  apiSessionToken.value = null
}

/** Keep the list open while the user keeps typing in the anchored input. */
const onPopoverInteractOutside = (event: Event) => {
  const detail = (event as CustomEvent<{ originalEvent?: Event }>).detail
  const target = detail?.originalEvent?.target as Node | null | undefined
  if (target && anchorRef.value?.contains(target)) {
    event.preventDefault()
  }
}

onMounted(async () => {
  await installGoogleMapsAPI()
})
</script>

<template>
  <div class="w-full autocomplete-container">
    <Popover v-if="!hasPlaceId" v-model:open="open" :modal="false">
      <PopoverAnchor as-child>
        <div ref="anchorRef" class="w-full">
          <AppFormInput
            id="autocompleteString"
            :model-value="autocompleteString"
            class="relative -top-1"
            type="search"
            name="autocompleteString"
            :placeholder="t('pages.settings.storeDetailPage.urls.googlePlaceId.field.placeholder')"
            @update:model-value="handleInputChange"
          >
            <template #icon-left>
              <BaseIcon name="IconoirGoogleCircle" class="h-5 w-5 icon-google" />
            </template>
          </AppFormInput>
        </div>
      </PopoverAnchor>

      <PopoverContent
        class="pointer-events-auto z-[1200] max-h-60 w-[var(--reka-popover-trigger-width)] overflow-auto p-0"
        align="start"
        :side-offset="4"
        @open-auto-focus.prevent
        @close-auto-focus.prevent
        @interact-outside="onPopoverInteractOutside"
      >
        <button
          v-for="prediction in predictions"
          :key="prediction.place_id"
          type="button"
          class="relative block w-full cursor-pointer select-none py-2 pl-3 pr-9 text-left text-foreground hover:bg-accent"
          @mousedown.prevent="selectPrediction(prediction)"
        >
          <div class="truncate font-normal">
            {{ prediction.structured_formatting.main_text }}
          </div>
          <div class="truncate text-muted-foreground">
            {{ prediction.structured_formatting.secondary_text }}
          </div>
        </button>
      </PopoverContent>
    </Popover>

    <div v-else class="flex">
      <span
        class="inline-flex w-11 shrink-0 items-center justify-center rounded-l-control border border-r-0 border-input bg-muted text-foreground"
      >
        <BaseIcon name="IconoirGoogleCircle" class="h-5 w-5 icon-google" />
      </span>
      <div class="relative flex min-w-0 grow items-center border border-input px-3 text-sm text-foreground">
        <BaseIcon name="HeroCheckIconOutline" class="mr-2 h-4 w-4 shrink-0 text-green-400" />
        <span class="truncate text-xs">
          {{ t('pages.settings.storeDetailPage.urls.googlePlaceId.yourLinkIsConfigured') }}
        </span>
      </div>
      <a
        :href="`http://search.google.com/local/writereview?placeid=${modelValue}`"
        target="_blank"
        class="relative -ml-px inline-flex shrink-0 items-center rounded-r-control border border-input bg-muted px-4 py-2 text-sm font-medium text-foreground hover:bg-accent focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary"
      >
        <BaseIcon name="HeroArrowTopRightOnSquareIconOutline" class="mr-1 h-5 w-5 text-muted-foreground" />
        {{ t('pages.settings.storeDetailPage.urls.googlePlaceId.test') }}
      </a>
    </div>

    <p v-show="hasPlaceId" class="mt-2 text-xs font-normal text-foreground">
      {{ t('pages.settings.storeDetailPage.urls.googlePlaceId.askWrongOne') }}
      <button type="button" class="cursor-pointer text-primary underline" @click="renewGooglePlaceId">
        {{ t('pages.settings.storeDetailPage.urls.googlePlaceId.changeIt') }}
      </button>
    </p>
  </div>
</template>
